import { ChangeDetectorRef, computed, Directive, EventEmitter, HostListener, Injector, Input, OnDestroy, OnInit, Output, signal, Type } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { Subscription } from 'rxjs';
import { AppConfig } from 'src/app/app-config.loader';
import { AnagraficaBaseService } from 'src/app/core/anagrafica-base.service';
import { AnagraficaClassificheService } from 'src/app/core/anagrafica-classifiche.service';
import { Indirizzo } from '../models/indirizzo.model';
import { Qualifica } from '../models/qualifica.model';
import { ClassificaI18nPipe } from '../pipes/classifica-i18n.pipe';

@Directive({
  selector: '[appAnagraficaAutocomplete]'
})
export class AnagraficaAutocompleteDirective<TItem extends Record<string, unknown>, TService extends AnagraficaBaseService<TItem>> implements OnInit, OnDestroy {
  @Input('appAnagraficaAutocomplete') serviceType!: Type<TService>;
  @Input() originAutocomplete: string | undefined;
  @Input() set qualifica(value: Qualifica | null | undefined) {
    this.qualificaSignal.set(value);
    this.updateInputValue();
  }

  @Input() filterCollection = (list: TItem[]): TItem[] => list;
  @Output() anagraficaItemsChanged = new EventEmitter<TItem[]>();
  @Output() unmatchedValue = new EventEmitter<string>();

  private service!: TService;
  private getAllSubscription: Subscription | undefined;
  private allItems: TItem[] | undefined;
  private classificai18n: ClassificaI18nPipe;
  private qualificaSignal = signal<Qualifica | null | undefined>(undefined);
  private labelRefreshSignal = signal(0);

  constructor(
    private appConfig: AppConfig,
    private injector: Injector,
    private autocompleteControl: AutoComplete,
    private cd: ChangeDetectorRef
  ) {
    this.classificai18n = new ClassificaI18nPipe(this.appConfig);
  }

  ngOnInit(): void {
    this.service = this.injector.get<TService>(this.serviceType);
    this.getAllSubscription = this.service.getAll().subscribe((a: TItem[]) => {
      if (a.length > 0 && (a[0] as unknown as Indirizzo).nomeIndirizzo && this.originAutocomplete !== 'ricercaAvanzataMittente') {
        a = a.filter(el => (el as unknown as Indirizzo).bloccato === false)
      }

      this.allItems = this.filterCollection(a);
      this.autocompleteControl.suggestions = this.filterCollection(a).slice(0, 100);
      this.anagraficaItemsChanged.emit(a);
      this.labelRefreshSignal.update(value => value + 1);
      this.updateInputValue();
    });

    if (this.autocompleteControl.autoHighlight === undefined) {
      this.autocompleteControl.autoHighlight = true;
    }

    if (this.autocompleteControl.dropdown === undefined) {
      this.autocompleteControl.dropdown = true;
    }

    if (this.autocompleteControl.optionLabel === undefined || this.autocompleteControl.optionLabel === null) {
      this.autocompleteControl.optionLabel = ((id: TItem) => this.getItemDescription(id)) as unknown as string;
    }
    this.configureInputValue();

    if (this.autocompleteControl.forceSelection === undefined) {
      this.autocompleteControl.forceSelection = true;
    }

    if (this.autocompleteControl.size === undefined) {
      this.autocompleteControl.size = 20;
    }

    this.autocompleteControl.delay = 20;
    this.updateInputValue();
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.getAllSubscription) {
      this.getAllSubscription.unsubscribe();
    }
  }

  @HostListener('completeMethod', ['$event.query'])
  onCompleteMethod(query: string): void {

    let q = query.toLowerCase();

    if (this.service instanceof AnagraficaClassificheService) {
      switch (q) {
        case 'rr':
          q = 'riservatissimo';
          break;
        case 's':
          q = 'segreto';
          break;
        case 'ss':
          q = 'segretissimo';
          break;
        case 'e':
          q = 'esercitazione';
          break;
        case 'nc':
          q = 'non classificato';
          break;
      }
    }

    const filtered: TItem[] = [];
    if (this.allItems) {
      for (const element of this.allItems) {
        if ((element[this.service.nameProperty] as string).toLowerCase().indexOf(q) !== -1) {
          filtered.push(element);
        }

        if (filtered.length === this.autocompleteControl.size) {
          break;
        }
      }
    }

    this.autocompleteControl.suggestions = filtered.filter(suggestion => {
      const isTranslatable = this.classificai18n.transform(suggestion[this.service.nameProperty] as string, this.qualificaSignal()?.qualificaId);
      return isTranslatable !== null;
    });
    // .map(i => {
    //   return this.service.getItemId(i);
    // });

    if (!this.autocompleteControl.forceSelection && this.autocompleteControl.suggestions.length === 0) {
      this.unmatchedValue.emit(query);
    }
  }

  private getItemDescription(item: TItem): string {
    if (!this.allItems || !item) { return '' }

    const value = item[this.service.nameProperty] as string;
    const qualifica = this.qualificaSignal();
    if (!qualifica) {
      return value;
    }
    const text = this.classificai18n.transform(value, qualifica.qualificaId);
    return text;
  }

  private updateInputValue(): void {
    this.autocompleteControl.updateInputValue();
    this.cd.markForCheck();
  }

  private configureInputValue(): void {
    this.autocompleteControl.inputValue = computed(() => {
      this.qualificaSignal();
      this.labelRefreshSignal();

      const modelValue = this.autocompleteControl.modelValue();
      const selectedOption = this.autocompleteControl.getSelectedOption(modelValue);
      if (modelValue) {
        if (typeof modelValue === 'object' || this.autocompleteControl.optionValue) {
          const label = this.autocompleteControl.getOptionLabel(selectedOption);
          return label != null ? label : modelValue;
        }
        return modelValue;
      }
      return '';
    });
  }
}
